import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import DashboardLayout from "../components/DashboardLayout";
import BrandDashboardLayout from "../components/BrandDashboardLayout";
import { MessageSquare, ArrowRight, Inbox as InboxIcon, Circle } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

export default function Inbox() {
  const { user } = useAuth();
  const [convos, setConvos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/conversations").then(({ data }) => setConvos(data)).finally(() => setLoading(false));
  }, []);

  const isBrand = user?.account_type === "brand";
  const Layout = isBrand ? BrandDashboardLayout : DashboardLayout;

  return (
    <Layout>
      <div className="p-6 md:p-12 max-w-5xl">
        <div className="mb-10">
          <div className="label-eyebrow">Conversations</div>
          <h1 className="font-heading text-4xl md:text-5xl uppercase tracking-tighter mt-2" data-testid="inbox-title">Partnership Inbox</h1>
          <p className="text-ink-muted mt-2 text-sm">
            {isBrand
              ? "All your athlete partnership conversations in one place."
              : "Brands reaching out to you about partnership opportunities."}
          </p>
        </div>

        {loading ? (
          <div className="surface p-12 text-center text-ink-muted">Loading…</div>
        ) : convos.length === 0 ? (
          <div className="surface p-12 text-center">
            <InboxIcon className="w-8 h-8 text-volt mx-auto mb-4" />
            <div className="font-heading text-2xl uppercase">No conversations yet</div>
            <p className="text-ink-muted text-sm mt-2 mb-6">
              {isBrand
                ? "Browse the athlete directory and start your first partnership."
                : "When brands open partnership conversations with you, they'll appear here."}
            </p>
            {isBrand && (
              <Link to="/app/brand/directory" className="btn-volt inline-flex items-center gap-2 text-sm" data-testid="goto-directory-btn">
                Browse directory <ArrowRight className="w-4 h-4" />
              </Link>
            )}
          </div>
        ) : (
          <div className="surface divide-y divide-line" data-testid="inbox-list">
            {convos.map(c => {
              const myUnread = isBrand ? c.unread_for_brand : c.unread_for_athlete;
              const counterpart = isBrand ? c.athlete_display : c.brand_display;
              const sub = isBrand ? c.athlete_sport : c.brand_category;
              return (
                <Link key={c.id} to={`/app/inbox/${c.id}`} className={`flex gap-4 p-5 hover:bg-bg-elev transition-colors ${myUnread ? "bg-bg-elev/40" : ""}`} data-testid={`inbox-item-${c.id}`}>
                  <div className="shrink-0 mt-1">
                    {myUnread ? <Circle className="w-2.5 h-2.5 fill-volt text-volt" /> : <Circle className="w-2.5 h-2.5 text-line" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3">
                      <div className="font-heading text-base uppercase tracking-tight truncate" data-testid={`inbox-counterpart-${c.id}`}>
                        {counterpart}{sub && <span className="text-ink-muted text-xs normal-case font-sans ml-2">{sub}</span>}
                      </div>
                      <div className="text-[10px] uppercase tracking-widest text-ink-muted shrink-0">{timeAgo(c.last_message_at)}</div>
                    </div>
                    <div className="text-sm mt-1 text-ink-muted truncate"><span className="text-white">{c.subject}</span></div>
                    <div className="text-xs text-ink-muted mt-1 truncate">{c.last_message_preview}</div>
                    <div className="flex gap-2 mt-2">
                      <Pill status={c.status} />
                      {c.budget_indication && <span className="text-[9px] uppercase tracking-widest text-ink-muted">{c.budget_indication}</span>}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}

function Pill({ status }) {
  const map = {
    open: { txt: "Open", cls: "border-volt text-volt" },
    accepted: { txt: "Accepted", cls: "border-[#34C759] text-[#34C759]" },
    declined: { txt: "Declined", cls: "border-[#FF3B30] text-[#FF3B30]" },
    closed: { txt: "Closed", cls: "border-line text-ink-muted" },
  };
  const s = map[status] || map.open;
  return <span className={`text-[9px] uppercase tracking-widest border px-2 py-0.5 ${s.cls}`}>{s.txt}</span>;
}
